import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, of, startWith } from 'rxjs';
import { Calendar } from 'src/app/models/centrex/calendar';
import { RingStates } from 'src/app/models/centrex/enums/ring-states';
import { RuleTargetTypes } from 'src/app/models/centrex/enums/rule-target-types';
import { Extension } from 'src/app/models/centrex/extension';
import { Rule } from 'src/app/models/centrex/rule';
import { Service } from 'src/app/models/centrex/service';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { RuleService } from 'src/app/services/centrex/rule.service';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatLabel, MatFormField } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-extension-rule',
    templateUrl: './extension-rule.component.html',
    styleUrls: ['./extension-rule.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatLabel,
        MatCheckbox,
        MatCheckboxModule,
        MatFormField,
        MatInput,
        MatSelect,
        MatOption,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class ExtensionRuleComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    extensionRuleInformations: Rule;
    extensionRuleForm: UntypedFormGroup;
    isCreation: boolean;
    calendars: Calendar[] = [];

    targetTypes = Object.keys(RuleTargetTypes);
    ruleTargetTypes = RuleTargetTypes;
    ringStates = Object.keys(RingStates);

    extensionId: string;
    extensionsList: Extension[] = [];
    selectedExtension: Extension;
    servicesList: Service[] = [];
    selectedService: Service;

    targetExtensionsOptions: Observable<Extension[]>;
    selectedTargetExtension: Extension;
    targetServicesOptions: Observable<Service[]>;
    selectedTargetService: Service;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private ruleService: RuleService,
        private calendarService: CalendarService,
        private organizationsService: OrganizationsService,
        private extensionService: ExtensionService,
        private serviceService: ServiceService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.createForm({} as Rule);
    }

    ngOnInit(): void {
        let ruleId: string;

        // Récupère l'identifiant de la règle
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            ruleId = params['id'];
            this.extensionId = params['extensionId'];
        });

        this.loadDatas(ruleId, this.extensionId);
    }

    loadDatas(ruleId: string, extensionId: string) {
        this.spinner.show();

        if (ruleId && extensionId) {
            this.isCreation = false;

            forkJoin([
                this.ruleService.getExtensionRule(extensionId, ruleId),
                this.calendarService.getCalendars(),
                this.extensionService.getExtensions(),
                this.serviceService.getServices(),
            ])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([extensionRule, calendars, extensions, services]) => {
                        this.extensionRuleInformations = extensionRule;
                        this.calendars = calendars;
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        this.selectedExtension = extensions.find((a) => a.id === this.extensionRuleInformations.extensionId);

                        if (this.extensionRuleInformations.targetType === RuleTargetTypes.extension) {
                            this.selectedTargetExtension = extensions.find((a) => a.id === this.extensionRuleInformations.target);
                        } else if (this.extensionRuleInformations.targetType === RuleTargetTypes.service) {
                            this.selectedTargetService = services.find((a) => a.name === this.extensionRuleInformations.target);
                        }

                        // Créé le formulaire
                        this.createForm(this.extensionRuleInformations);
                    },
                    error: () => {
                        this.createForm(this.extensionRuleInformations);
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.extensionRuleInformations = new Rule();
            this.extensionRuleInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';
            this.extensionRuleInformations.enabled = true;
            this.extensionRuleInformations.extensionId = extensionId;

            forkJoin([this.calendarService.getCalendars(), this.extensionService.getExtensions(), this.serviceService.getServices()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([calendars, extensions, services]) => {
                        this.calendars = calendars;
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        this.selectedExtension = extensions.find((a) => a.id === this.extensionId);

                        // Créé le formulaire
                        this.createForm(this.extensionRuleInformations);
                    },
                    error: () => {
                        this.createForm(this.extensionRuleInformations);
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm(rule: Rule) {
        this.extensionRuleForm = this.formbuilder.group({
            id: [rule.id],
            organizationId: [rule.organizationId],
            enabled: [rule.enabled],
            ringState: [rule.ringState],
            ringSeconds: [rule.ringSeconds],
            targetType: [rule.targetType],
            target: [rule.target],
            calendarId: [rule.calendarId],
            extensionId: [rule.extensionId],
            extension: [this.extensionDisplayPipe.transform(this.selectedExtension)],
            service: [this.selectedService],
            extensionTarget: [this.selectedTargetExtension, [this.autocompleteExtensionValidator()]],
            serviceTarget: [this.selectedTargetService, [this.autocompleteServiceValidator()]],
        });

        this.targetExtensionsOptions = this.extensionRuleForm.get('extensionTarget').valueChanges.pipe(
            startWith<string | Extension>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : value ? this.extensionDisplayPipe.transform(value) : null;
                return name ? this.extensionTargetSearch(name as string) : this.extensionsList.slice();
            })
        );

        this.targetServicesOptions = this.extensionRuleForm.get('serviceTarget').valueChanges.pipe(
            startWith<string | Service>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : null;
                return name ? this.serviceTargetSearch(name as string) : this.servicesList.slice();
            })
        );

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.ruleService
                .addExtensionRule(this.extensionRuleInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('ExtensionRule.SuccessMessageCreateGlobalRule'));

                        // Redirige sur la page des règles
                        this.router.navigate(['/extension'], {
                            queryParams: { id: this.extensionId },
                            state: { tab: 'rule' },
                        });

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('ExtensionRule.ErrorMessageCreateGlobalRule'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.ruleService
                .updateExtensionRule(this.extensionRuleInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('ExtensionRule.SuccessMessageUpdateGlobalRule'));

                        this.router.navigate(['/extension'], {
                            queryParams: { id: this.extensionId },
                            state: { tab: 'rule' },
                        });

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('ExtensionRule.ErrorMessageUpdateGlobalRule'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.extensionRuleInformations.enabled = this.extensionRuleForm.value.enabled;
        this.extensionRuleInformations.ringState = this.extensionRuleForm.value.ringState;
        this.extensionRuleInformations.ringSeconds = this.extensionRuleForm.value.ringSeconds;
        this.extensionRuleInformations.targetType = this.extensionRuleForm.value.targetType;
        this.extensionRuleInformations.target = this.extensionRuleForm.value.target;
        this.extensionRuleInformations.calendarId = this.extensionRuleForm.value.calendarId;
        this.extensionRuleInformations.extensionId = this.extensionRuleForm.value.extensionId;
    }

    displayExtension = (extension: Extension): string => {
        return extension ? this.extensionDisplayPipe.transform(extension) : '';
    };

    displayService = (service: Service): string => {
        return service ? service.name : '';
    };

    targetTypeChange(targetType: string) {
        this.extensionRuleForm.get('target').setValue('');
        this.extensionRuleForm.get('extensionTarget').setValue('');
        this.extensionRuleForm.get('serviceTarget').setValue('');
    }

    extensionsTargetChange(event: MatAutocompleteSelectedEvent) {
        let extension = event.option.value as Extension;
        if (extension) {
            this.extensionRuleForm.get('target').setValue(extension.id);
            this.selectedTargetExtension = extension;
        }
    }

    servicesTargetChange(event: MatAutocompleteSelectedEvent) {
        let service = event.option.value as Service;
        if (service) {
            this.extensionRuleForm.get('target').setValue(service.name);
            this.selectedTargetService = service;
        }
    }

    private extensionTargetSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.extensionsList.filter((a) => this.extensionDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private serviceTargetSearch(value: string): Service[] {
        const filterValue = value.toLowerCase();

        return this.servicesList.filter((a) => a.name.toLowerCase().includes(filterValue));
    }

    private autocompleteExtensionValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.extensionsList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }

    private autocompleteServiceValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.servicesList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
